/**
 * Scouting report display utilities
 *
 * Shared logic for computing display text from scout report data.
 * Extracted from ReportItemContainer to avoid duplication between
 * the Reports page and the player detail Reports & Notes tab.
 */

import type { PlayerDetailScoutReportsFragment } from "@/generated/graphql";

import type { ScoutingReportType } from "../types";

/**
 * Compute the ground prefix for a match
 * @param ground - Match ground value ("home", "away", or null)
 * @returns "vs" for home, "@" for away, empty string otherwise
 */
const getGroundPrefix = (ground?: string | null): string => {
  if (!ground) return "";
  const normalized = ground.toLowerCase();
  if (normalized === "home") return "vs";
  if (normalized === "away") return "@";
  return "";
};

/**
 * Derive the display text for a scout report based on match/opponent data.
 *
 * Priority: uses structured `matches` array when available, falls back to
 * legacy `opponent`/`ground` fields, and defaults to "General Report" when
 * no opponent information exists.
 * @param report - GraphQL scout report fragment
 * @returns Human-readable text such as "vs Arsenal", "@ Chelsea",
 *   "General Report", or a comma-separated list for multi-match reports
 */
export const getScoutReportDisplayText = (
  report: PlayerDetailScoutReportsFragment
): string => {
  if (report.matches?.length) {
    const validMatches = report.matches.filter(
      (match): match is NonNullable<typeof match> => !!match?.opponent
    );

    const hasMultipleMatches =
      validMatches.length > 1 ||
      validMatches.some(match => /multi/i.test(match.opponent ?? ""));

    if (hasMultipleMatches) {
      return validMatches
        .map(match => {
          const prefix = getGroundPrefix(match.ground);
          const teamName = match.opponent?.replace(/multi/gi, "Multiple") ?? "";
          return `${prefix} ${teamName}`.trim();
        })
        .join(", ");
    }

    const match = validMatches[0];
    if (!match?.opponent) return "General Report";

    const prefix = getGroundPrefix(match.ground);
    return `${prefix} ${match.opponent}`.trim();
  }

  if (report.opponent) {
    if (/multi/i.test(report.opponent)) {
      return "Multiple Matches";
    }

    const prefix = getGroundPrefix(report.ground);
    return `${prefix} ${report.opponent}`.trim();
  }

  return "General Report";
};

/**
 * Determine the report type based on display text.
 *
 * Returns "General Report" when the display text indicates no opponent,
 * otherwise defaults to "Match Report".
 * @param displayText - Text produced by `getScoutReportDisplayText`
 * @returns The report type classification
 */
export const getScoutReportType = (displayText: string): ScoutingReportType =>
  displayText === "General Report" ? "General Report" : "Match Report";
